import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import cl from 'classnames';
import { motion } from 'framer-motion';
import { noop, range } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useInterval, useMeasure, useRafState } from 'react-use';
import styled from 'styled-components';

import { deviceBreakpoints, useDeviceDetect } from '../../hooks/useDeviceQuery.hook';
import { Avatar } from '../Avatar';
import { dolDinhLucAuthor } from '../Blog/dolDinhLucAuthor.data';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { MODEL_CLASS } from '../Mobile/Modal/ModelConstant';
import { useDolStoryStore } from './dol-story.store';
import { StoryCard, StoryCardItem } from './StoryCard';

const BottomSheetContent = styled.div`
  height: 100%;
  overflow: hidden;
`;

const StyledBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  display: none;
  z-index: -1;
  @supports (bottom: env(safe-area-inset-bottom)) {
    bottom: env(safe-area-inset-bottom);
  }

  &.visible {
    display: block;
    z-index: 9999;
  }

  [data-rsbs-backdrop] {
    background: rgba(0, 0, 0, 0.9);
  }

  [data-rsbs-overlay] {
    background: transparent;
  }
  [data-rsbs-header] {
    display: none;
  }
`;

const Main = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  justify-content: center;
  gap: 24px;
  height: 100%;

  .navigation-action {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${colorsV2.gray20};
    z-index: 100;
    display: none;
    color: ${colorsV2.black80};
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 20px;
    &.disabled {
      opacity: 0;
      visibility: hidden;
    }
    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
  }

  @media ${deviceBreakpoints.fromTablet} {
    grid-template-columns: max-content minmax(0, max-content) max-content;
    .navigation-action {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  .story-card {
    display: none;
    &.active {
      display: grid;
    }
  }
`;

export interface StoryModalProps {
  data: StoryCardItem[];
  visible: boolean;
  onClose: () => void;
  activeIndex: number;
  onNextClick?: () => void;
  onPrevClick?: () => void;
}

const CardSlideMain = styled.div`
  position: relative;
  height: 100%;
  .card-slides-header {
    display: grid;
    gap: 12px;
    z-index: 4;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 12px 16px;
    .author-n-close {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, max-content);
      justify-content: space-between;
      .author-data {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: minmax(0, max-content);
        gap: 8px;
        align-items: center;
      }
      .btn-close {
        cursor: pointer;
        z-index: 20;
        font-size: 18px;
        line-height: 0;
        display: grid;
        place-items: center;
        color: ${colorsV2.white100};
        transition: all 0.3s ease;
        &:hover {
          transform: scale(1.1);
        }
        &:active {
          transform: scale(0.9);
        }
      }
    }
    .slide-progress-list {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      gap: 2px;
    }
    .slide-progress {
      height: 2px;
      position: relative;
      border-radius: 1px;
      overflow: hidden;
      .slide-progress-child {
        position: absolute;
        top: 0;
        bottom: 0;
      }

      .slide-progress-trail {
        background: ${colorsV2.white60};
        width: 100%;
      }
      .slide-progress-path-wrapper {
        position: relative;
        height: 100%;
      }
      .slide-progress-path {
        background: ${colorsV2.red100};
        transition: all 0.1s linear;
        transform-origin: left;
      }
    }
  }
`;

const TIME_OUT = 5000;

interface SlideProgressPathProps {
  dataIndex: number;
  activeIndex: number;
  onEnded?: () => void;
}

const userAuthor = {
  avatar: {
    src: dolDinhLucAuthor.avatarUrl,
    size: 20,
    alt: dolDinhLucAuthor.name,
  },
};

const SlideProgressPath = ({ dataIndex, activeIndex, onEnded = noop }: SlideProgressPathProps) => {
  const [percent, setPercent] = useRafState(0);
  const startTime = React.useRef(0);
  const [ref, { width }] = useMeasure();

  useInterval(
    () => {
      if (startTime.current > 0) {
        const now = new Date().getTime();
        const elapseTime = now - startTime.current;
        if (elapseTime <= TIME_OUT) {
          const rawPercent = (elapseTime / TIME_OUT) * 100;
          setPercent(Math.round(rawPercent));
        } else {
          setPercent(100);
          startTime.current = 0;
          onEnded();
        }
      }
    },
    activeIndex === dataIndex ? 100 : undefined
  );

  React.useEffect(() => {
    if (activeIndex === dataIndex) {
      startTime.current = new Date().getTime();
      setPercent(0);
      return;
    }
    startTime.current = 0;
    if (activeIndex > dataIndex) {
      setPercent(100);
      // there will be rare case when useInterval conflict with this effect, so set timeout is needed
      setTimeout(() => {
        setPercent(100);
      }, 101);
      return;
    }
    setPercent(0);
    // there will be rare case when useInterval conflict with this effect, so set timeout is needed
    setTimeout(() => {
      setPercent(0);
    }, 101);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, dataIndex]);
  return (
    <div className="slide-progress-path-wrapper" ref={ref}>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: width !== undefined ? percent / 100 : 0 }}
        className="slide-progress-child slide-progress-path"
        style={{ width }}
      />
    </div>
  );
};

const SlideProgressList = observer(() => {
  const store = useDolStoryStore();
  return (
    <div className="slide-progress-list">
      {range(store?.data?.length || 0).map((dataIndex) => {
        return (
          <div key={dataIndex} className="slide-progress">
            <div className="slide-progress-child slide-progress-trail" />
            <SlideProgressPath
              onEnded={store.nextSlide}
              dataIndex={dataIndex}
              activeIndex={store.currentActiveIndex}
            />
          </div>
        );
      })}
    </div>
  );
});

export const StoryModal = (props: StoryModalProps) => {
  const { visible, onClose, data, activeIndex, onNextClick = noop, onPrevClick = noop } = props;
  const { isMobile } = useDeviceDetect();
  return (
    <StyledBottomSheet className={cl(MODEL_CLASS, { visible })}>
      <div aria-hidden data-rsbs-backdrop onClick={onClose} />
      <BottomSheetContent>
        <Main id="story-modal">
          <div
            aria-hidden
            onClick={() => {
              if (activeIndex === 0) {
                return;
              }
              onPrevClick();
            }}
            className={cl('navigation-action navigation-prev', { disabled: activeIndex === 0 })}
          >
            <IconChevronChevronLeft />
          </div>

          <CardSlideMain className="card-slides">
            <div className="card-slides-header">
              <SlideProgressList />
              <div className="author-n-close">
                <div className="author-data">
                  <Avatar user={userAuthor} />
                  <Typography.Paragraph variant="semi-bold/12" color="white100">
                    {dolDinhLucAuthor.name}
                  </Typography.Paragraph>
                </div>
                <div aria-hidden onClick={onClose} className="btn-close">
                  <IconOtherClose />
                </div>
              </div>
            </div>
            {data.map((card, cardIndex) => {
              return (
                <StoryCard
                  onClick={(e) => {
                    if (!isMobile) return;
                    const x = e.pageX;
                    const boundaryWidth = window.innerWidth;
                    // 1. if click on right boundary -> next slide
                    if (x > boundaryWidth / 2) {
                      onNextClick();
                      return;
                    }
                    // 2. else -> previous slide
                    onPrevClick();
                  }}
                  key={card.id}
                  className={cl({ active: activeIndex === cardIndex })}
                  variant="full"
                  {...card}
                />
              );
            })}
          </CardSlideMain>

          <div
            aria-hidden
            onClick={() => {
              if (activeIndex === data.length - 1) {
                return;
              }
              onNextClick();
            }}
            className={cl('navigation-action navigation-next', {
              disabled: activeIndex === data.length - 1,
            })}
          >
            <IconChevronChevronRight />
          </div>
        </Main>
      </BottomSheetContent>
    </StyledBottomSheet>
  );
};
